import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  CircularProgress,
  makeStyles
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import LaunchIcon from "@material-ui/icons/Launch";
import ExploreOutlinedIcon from "@material-ui/icons/ExploreOutlined";
import ReactDOMServer from "react-dom/server";
import { useTheme } from "styled-components";

import NewDataTable, { DataTableColumns, DataTableActions } from "@molecules/NewDataTable";
import ButtonTable from "@components/Button/ButtonTable";
import useTranslation from "src/translations/useTranslation";
import { TravelAnalysisMessages } from "@shared/languages/interfaces";
import utils from "@utils/useful-functions";

import { TravelAnalysisAlertRow } from "../../types";
import AlertMetadataDialog from "./AlertMetadataDialog";
import TravelCoordinateViewModal from "./TravelCoordinateViewModal";

const useStyles = makeStyles(() => ({
  dialogPaper: {
    height: "90vh",
    maxHeight: "90vh"
  },
  dialogContent: {
    padding: "16px",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column" as const,
    flex: 1,
    "& .dataTables_scroll": {
      flex: 1,
      display: "flex",
      flexDirection: "column" as const,
      overflow: "hidden"
    },
    "& .dataTables_scrollHead": {
      flexShrink: 0
    },
    "& .dataTables_scrollBody": {
      flex: 1,
      maxHeight: "none !important",
      overflow: "auto !important" as any
    },
    "& .dataTables_wrapper": {
      display: "flex",
      flexDirection: "column" as const,
      flex: 1,
      overflow: "hidden"
    },
    "& .dataTables_search": {
      flexShrink: 0
    },
    "& .dataTables_info": {
      padding: "4px 0 !important",
      fontSize: "12px"
    },
    "& .dataTables_paginate": {
      padding: "2px 0 !important",
      margin: 0
    },
    "& .fixed--bottom": {
      position: "relative !important" as any,
      bottom: "auto !important" as any,
      margin: 0,
      width: "100%",
      padding: "4px 15px 0",
      background: "white",
      flexShrink: 0
    }
  },
  loadMoreLink: {
    display: "block",
    textAlign: "right" as const,
    cursor: "pointer",
    fontSize: "13px",
    padding: "0 10px 25px",
    flexShrink: 0,
    "&:hover": {
      textDecoration: "underline"
    }
  },
  loadMoreDisabled: {
    display: "block",
    textAlign: "right" as const,
    fontSize: "13px",
    padding: "0 10px 25px",
    flexShrink: 0,
    opacity: 0.4,
    cursor: "default"
  }
}));

/** Props for the AlertsDrilldownModal organism. */
interface AlertsDrilldownModalProps {
  open: boolean;
  onClose: () => void;
  alerts: TravelAnalysisAlertRow[];
  loading: boolean;
  totalCount: number;
  selectedAlertType: { id: string; description: string } | null;
  onLoadMore: () => void;
  onOpenTravel?: (travelId: string) => void;
}

/**
 * Modal dialog that displays a detailed list of travel analysis alerts.
 * Opened by clicking a chart bar (filtered by alert type) or the Alerts KPI card (all alerts).
 * Uses NewDataTable with server-side pagination via "Load more" button.
 */
const AlertsDrilldownModal: React.FC<AlertsDrilldownModalProps> = ({
  open,
  onClose,
  alerts,
  loading,
  totalCount,
  selectedAlertType,
  onLoadMore,
  onOpenTravel
}) => {

  const { t } = useTranslation();
  const classes = useStyles();
  const theme = useTheme() as any;

  const [metadataAlert, setMetadataAlert] = useState<TravelAnalysisAlertRow | null>(null);
  const [coordinateViewRow, setCoordinateViewRow] = useState<TravelAnalysisAlertRow | null>(null);

  const title = selectedAlertType
    ? `${t(TravelAnalysisMessages.alertsListTitle)} — ${selectedAlertType.description}`
    : t(TravelAnalysisMessages.alertsListTitle);
  const columns: DataTableColumns[] = [
    {
      title: t(TravelAnalysisMessages.columnNumDoc),
      data: (row: TravelAnalysisAlertRow) => row.numDoc || "—",
      filterable: true,
      propertyName: "numDoc"
    },
    {
      title: t(TravelAnalysisMessages.columnVehicle),
      data: (row: TravelAnalysisAlertRow) => `${row.vehicle.code} — ${row.vehicle.description}`,
      filterable: true,
      propertyName: "vehicle"
    },
    {
      title: t(TravelAnalysisMessages.columnAlertType),
      data: (row: TravelAnalysisAlertRow) => row.alertType.description,
      filterable: true,
      propertyName: "alertType"
    },
    {
      title: t(TravelAnalysisMessages.columnOrigin),
      data: (row: TravelAnalysisAlertRow) =>
        row.origin.name || `${row.origin.latitude}, ${row.origin.longitude}`,
      filterable: false
    },
    {
      title: t(TravelAnalysisMessages.columnDestination),
      data: (row: TravelAnalysisAlertRow) => {
        if (!row.destination) return "—";

        return row.destination.name || `${row.destination.latitude}, ${row.destination.longitude}`;
      },
      filterable: false
    },
    {
      title: t(TravelAnalysisMessages.columnDate),
      data: (row: TravelAnalysisAlertRow) =>
        utils.formatDateIfHave(row.registrationDate, "fullDate"),
      filterable: false
    },
    {
      title: t(TravelAnalysisMessages.columnActions),
      orderable: false,
      searchable: false,
      data: () => ReactDOMServer.renderToString(
        <span style={{ display: "flex", gap: "4px", justifyContent: "center" }}>
          <ButtonTable className="btn-alert-details" aria-disabled>
            <InfoOutlinedIcon fontSize="small" />
          </ButtonTable>
          <ButtonTable className="btn-open-travel" aria-disabled>
            <LaunchIcon fontSize="small" />
          </ButtonTable>
          <ButtonTable className="btn-coordinate-view" aria-disabled>
            <ExploreOutlinedIcon fontSize="small" />
          </ButtonTable>
        </span>
      ),
      filterable: false
    }
  ];

  const actions: DataTableActions<TravelAnalysisAlertRow>[] = [
    {
      ref: ".btn-alert-details",
      callback: (row: TravelAnalysisAlertRow) => setMetadataAlert(row)
    },
    {
      ref: ".btn-open-travel",
      callback: (row: TravelAnalysisAlertRow) => {
        if (onOpenTravel) onOpenTravel(row.travelId);
      }
    },
    {
      ref: ".btn-coordinate-view",
      callback: (row: TravelAnalysisAlertRow) => setCoordinateViewRow(row)
    }
  ];
  const hasMore = alerts.length < totalCount;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth scroll="paper" classes={{ paper: classes.dialogPaper }}>
        <DialogTitle>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>{title}</span>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent dividers className={classes.dialogContent}>
          {loading && alerts.length === 0 ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
              <CircularProgress size={32} />
            </div>
          ) : alerts.length === 0 ? (
            <div style={{ textAlign: "center", padding: 40, color: "#999" }}>
              {t(TravelAnalysisMessages.noAlertsFound)}
            </div>
          ) : (
            <>
              <span
                role="button"
                tabIndex={hasMore && !loading ? 0 : -1}
                className={hasMore ? classes.loadMoreLink : classes.loadMoreDisabled}
                style={{ color: theme.colors.primary, opacity: loading ? 0.6 : hasMore ? 1 : 0.4 }}
                onClick={hasMore && !loading ? onLoadMore : undefined}
                onKeyDown={(e) => { if (e.key === "Enter" && hasMore && !loading) onLoadMore(); }}
              >
                {loading
                  ? t(TravelAnalysisMessages.loadMore) + "..."
                  : hasMore
                    ? `${t(TravelAnalysisMessages.loadMore)} (${alerts.length}/${totalCount})`
                    : `${t(TravelAnalysisMessages.loadMore)} (${totalCount}/${totalCount})`
                }
              </span>
              <NewDataTable
                data={alerts}
                columns={columns}
                actions={actions}
                customDataCount={totalCount}
                use="dialogScreen"
                showSearching
                filters={false}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary">
            {t(TravelAnalysisMessages.close)}
          </Button>
        </DialogActions>
      </Dialog>
      <AlertMetadataDialog
        alert={metadataAlert}
        onClose={() => setMetadataAlert(null)}
      />
      <TravelCoordinateViewModal
        open={!!coordinateViewRow}
        onClose={() => setCoordinateViewRow(null)}
        travelId={coordinateViewRow?.travelId || ""}
        vehicleDescription={coordinateViewRow ? `${coordinateViewRow.vehicle.code} — ${coordinateViewRow.vehicle.description}` : ""}
        numDoc={coordinateViewRow?.numDoc}
      />
    </>
  );
};

export default AlertsDrilldownModal;
